import React from 'react';

/**
 * 화면에 쓰는 **작은 그림들**.
 *
 * 예전에는 💬 👤 🔗 처럼 이모지 글자를 그대로 찍었다. 이모지는 기기마다 그림이 달라서
 * (아이폰의 💬 와 갤럭시의 💬 는 생김새가 다르다) 같은 화면이 폰마다 달라 보이고,
 * `color` 도 안 먹어서 글자색을 맞출 수 없다 — 꺾쇠(`Caret`)를 고친 것과 같은 이유다.
 *
 * 그래서 그림으로 그린다. 크기는 `1em` 이라 부모의 `fontSize` 를, 색은 `currentColor` 라
 * 부모의 `color` 를 그대로 따른다. 쓰던 자리에 그대로 끼우면 된다.
 */
type IconProps = { style?: React.CSSProperties; title?: string };

function Svg({ style, title, children }: IconProps & { children: React.ReactNode }) {
    return (
        <svg
            width="1em"
            height="1em"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            role={title ? 'img' : undefined}
            aria-hidden={title ? undefined : true}
            aria-label={title}
            style={{ display: 'inline-block', verticalAlign: '-0.125em', ...style }}
        >
            {children}
        </svg>
    );
}

/** 💬 댓글 */
export function IconComment(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M2.5 4.2a1.7 1.7 0 0 1 1.7-1.7h7.6a1.7 1.7 0 0 1 1.7 1.7v5a1.7 1.7 0 0 1-1.7 1.7H6.6L3.4 13.3v-2.4h-.9V4.2Z" />
        </Svg>
    );
}

/**
 * 💭 아직 없음(빈 상태).
 *
 * 구름 모양 생각풍선으로 그렸다가 바꿨다 — 11px 로 줄이면 뭉개져서 사탕이나 돋보기처럼
 * 보였다. 말풍선 안에 점 셋이면 작아도 "아직 말이 없다" 로 읽힌다.
 */
export function IconThought(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M2.5 4.2a1.7 1.7 0 0 1 1.7-1.7h7.6a1.7 1.7 0 0 1 1.7 1.7v5a1.7 1.7 0 0 1-1.7 1.7H6.6L3.4 13.3v-2.4h-.9V4.2Z" />
            <path d="M5.6 6.7h.01M8 6.7h.01M10.4 6.7h.01" strokeWidth="1.8" />
        </Svg>
    );
}

/** 👤 사람(프로필) */
export function IconUser(p: IconProps) {
    return (
        <Svg {...p}>
            <circle cx="8" cy="5.4" r="2.8" />
            <path d="M2.9 13.4a5.1 5.1 0 0 1 10.2 0" />
        </Svg>
    );
}

/** 🔗 링크 */
export function IconLink(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M6.6 9.4a2.7 2.7 0 0 0 4 .3l1.9-1.9a2.7 2.7 0 0 0-3.8-3.8l-1.1 1" />
            <path d="M9.4 6.6a2.7 2.7 0 0 0-4-.3L3.5 8.2a2.7 2.7 0 0 0 3.8 3.8l1.1-1" />
        </Svg>
    );
}

/** 🔒 로그인 아이디 (프로필 창 [아이디 변경] — 아이디가 비밀번호 몫이라 자물쇠, 사용자 2026-10-27 초안 7번) */
export function IconLock(p: IconProps) {
    return (
        <Svg {...p}>
            <rect x="3.4" y="7.2" width="9.2" height="6.6" rx="1.4" />
            <path d="M5.4 7.2V5.3a2.6 2.6 0 0 1 5.2 0v1.9" />
            <path d="M8 9.8v1.6" />
        </Svg>
    );
}

/** 📊 통계 */
export function IconChart(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M3.4 13.2V7.6M8 13.2V3.4M12.6 13.2V9.6" />
        </Svg>
    );
}

/** ❌ 오류 */
export function IconX(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6" />
        </Svg>
    );
}

/** 📝 기록(전투 수) */
export function IconMemo(p: IconProps) {
    return (
        <Svg {...p}>
            <path d="M12.6 7.4v5.1a1 1 0 0 1-1 1H4.4a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1h5" />
            <path d="M11.1 2.6l2.3 2.3-4.2 4.2-2.6.3.3-2.6 4.2-4.2Z" />
        </Svg>
    );
}

/** 📅 날짜 */
export function IconCalendar(p: IconProps) {
    return (
        <Svg {...p}>
            <rect x="2.6" y="3.6" width="10.8" height="9.8" rx="1.4" />
            <path d="M2.6 6.6h10.8M5.6 2.4v2.2M10.4 2.4v2.2" />
        </Svg>
    );
}
